import { useMemo } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { MapContainer } from 'react-leaflet';
import { Paper, styled } from '@mui/material';

import MapWithNavigation from '../home/LazyMapWithNavigation';
import Directions from '../home/leaflet/Directions';
import { TripLegAddress } from '@/data/models/reservation.model';
import {
  ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';

const PREFIX = 'ReservationSummaryMap';

const classes = {
  mapContainer: `${PREFIX}-mapContainer`,
  map: `${PREFIX}-map`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.mapContainer}`]: {
    // Match the summary card's gutters on small screens; the wider desktop
    // padding leaves too little room for the map on a phone.
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingBottom: theme.spacing(5),
    },
  },
  [`& .${classes.map}`]: {
    width: '100%',
    height: 300,
    '& .leaflet-routing-container .leaflet-routing-alternatives-container': {
      display: 'none',
    },
    '& .leaflet-control-container .leaflet-bottom.leaflet-right': {
      display: 'none',
    },
    '& .leaflet-marker-pane .leaflet-marker-icon.pickup_icon': {
      width: 30,
    },
    '& .leaflet-marker-pane .leaflet-marker-icon.dropoff_icon': {
      width: 25,
    },
  },
}));

interface ReservationSummaryMapProps {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
}

const ReservationSummaryMap = ({ form }: ReservationSummaryMapProps) => {
  const trips = form.watch('trips');

  const tripLegsForMap = useMemo(
    () =>
      trips.map((trip) => ({
        pickupAddress: JSON.parse(trip.pickupAddressStruct || '{}'),
        dropoffAddress: JSON.parse(trip.dropoffAddressStruct || '{}'),
      })),
    [trips],
  );

  const mapCenter = useMemo(() => {
    const firstLeg = tripLegsForMap[0];
    if (
      !firstLeg?.pickupAddress?.latitude ||
      !firstLeg?.pickupAddress?.longitude
    ) {
      return { lat: 39.8283, lng: -98.5795 };
    }
    return {
      lat: firstLeg.pickupAddress.latitude,
      lng: firstLeg.pickupAddress.longitude,
    };
  }, [tripLegsForMap]);

  const firstLeg = tripLegsForMap[0];

  return (
    <StyledPaper className={classes.mapContainer}>
      <MapContainer className={classes.map} center={mapCenter} zoom={10}>
        <MapWithNavigation
          tripLegs={tripLegsForMap}
          driverLocation={null}
          fitBounds
        />
        {trips.length > 0 && firstLeg && (
          <Directions
            origin={{
              lat: firstLeg.pickupAddress.latitude || 0,
              lng: firstLeg.pickupAddress.longitude || 0,
            }}
            destination={{
              lat: firstLeg.dropoffAddress.latitude || 0,
              lng: firstLeg.dropoffAddress.longitude || 0,
            }}
          />
        )}
      </MapContainer>
    </StyledPaper>
  );
};

export { ReservationSummaryMap, type ReservationSummaryMapProps };
